import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role, TeamUser } from '@crosscastr/shared';
import { api } from '../lib/api.js';
import { Icon } from '../components/Icon.js';

// "צוות ומשתמשים" — owner/admin manage the team: create members (email + name + E.164 phone for the
// SMS OTP + role), change roles, activate/deactivate. The API is the security boundary; this page
// only reflects it (and hides controls an admin can't use). Password hashes are never shown; a
// generated initial password is displayed ONCE right after creation.

const card: React.CSSProperties = { padding: 24, marginBottom: 20 };
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' };
const label: React.CSSProperties = { fontSize: 13, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600 };
const input: React.CSSProperties = {
  height: 42, padding: '0 14px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)',
  background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 14, minWidth: 200,
};
const ROLE_LABEL: Record<Role, string> = { owner: 'בעלים', admin: 'מנהל', agent: 'סוכן' };
const ROLE_CHIP: Record<Role, string> = { owner: 'gold', admin: 'green', agent: 'muted' };

function canManage(actor: Role | undefined, targetRole: Role): boolean {
  if (actor === 'owner') return true;
  if (actor === 'admin') return targetRole === 'agent';
  return false;
}

export function TeamPage(): JSX.Element {
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', phone: '', role: 'agent' as Role });
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const flash = (t: string): void => { setToast(t); setTimeout(() => setToast(null), 3000); };
  const invalidate = (): void => void qc.invalidateQueries({ queryKey: ['users'] });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const myRole = me?.user.role;
  const { data: users, isLoading, error } = useQuery({ queryKey: ['users'], queryFn: () => api.users() });

  const create = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: (r) => {
      invalidate();
      setShowForm(false);
      setForm({ email: '', name: '', phone: '', role: 'agent' });
      if (r.initialPassword) setIssued({ email: r.user.email, password: r.initialPassword });
      flash(`המשתמש ${r.user.email} נוצר`);
    },
    onError: () => flash('יצירת המשתמש נכשלה — בדוק אימייל/טלפון/הרשאה'),
  });
  const update = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof api.updateUser>[1] }) => api.updateUser(v.id, v.body),
    onSuccess: () => { invalidate(); flash('עודכן'); },
    onError: () => flash('העדכון נכשל'),
  });

  if (error) {
    // The API 403s an agent — reflect that instead of an empty table.
    return (
      <>
        <div className="page-head"><div><div className="eyebrow">ניהול</div><h1>משתמשים</h1></div></div>
        <div className="card" style={card}>אין לך הרשאה לנהל משתמשים.</div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">ניהול</div>
          <h1>משתמשים</h1>
          <div className="sub">הוספת חברי צוות, שיוך תפקידים והרשאות</div>
        </div>
        <button className="btn" onClick={() => setShowForm((v) => !v)}>
          <Icon name="check" /> משתמש חדש
        </button>
      </div>

      {/* one-time initial password */}
      {issued && (
        <div className="card" style={{ ...card, borderColor: 'var(--accent-green)' }}>
          <div style={{ ...label, color: 'var(--accent-green)' }}>סיסמה ראשונית עבור {issued.email} — מוצגת פעם אחת בלבד</div>
          <div style={row}>
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: 16, padding: '8px 14px', borderRadius: 'var(--r-sm)', background: 'var(--surface-1)', border: '1px solid var(--hair)' }} dir="ltr">
              {issued.password}
            </code>
            <button className="btn ghost" onClick={() => { void navigator.clipboard?.writeText(issued.password); flash('הועתק'); }}>העתק</button>
            <button className="btn ghost" style={{ marginInlineStart: 'auto' }} onClick={() => setIssued(null)}>סגור</button>
          </div>
          <div style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 10 }}>מסור אותה למשתמש בערוץ מאובטח. הוא יתבקש להחליף אותה בכניסה הראשונה.</div>
        </div>
      )}

      {/* create form */}
      {showForm && (
        <div className="card" style={card}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>משתמש חדש</h3>
          <div style={{ ...row, alignItems: 'flex-end' }}>
            <div>
              <div style={label}>אימייל</div>
              <input style={{ ...input }} type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <div style={label}>שם</div>
              <input style={{ ...input }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <div style={label}>טלפון (לאימות בכניסה)</div>
              <input style={{ ...input }} type="tel" dir="ltr" placeholder="+9725…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <div style={label}>תפקיד</div>
              <select style={{ ...input }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
                <option value="agent">סוכן</option>
                <option value="admin" disabled={myRole !== 'owner'}>מנהל</option>
                <option value="owner" disabled={myRole !== 'owner'}>בעלים</option>
              </select>
            </div>
            <button className="btn" onClick={() => create.mutate()} disabled={create.isPending || !form.email || !form.name || !form.phone}>
              {create.isPending ? 'יוצר…' : 'צור משתמש'}
            </button>
          </div>
        </div>
      )}

      {/* users list */}
      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: 24, color: 'var(--text-dim)' }}>טוען…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'start', color: 'var(--text-dim)', fontSize: 12 }}>
                <th style={{ padding: '14px 20px', textAlign: 'start' }}>משתמש</th>
                <th style={{ padding: '14px 20px', textAlign: 'start' }}>תפקיד</th>
                <th style={{ padding: '14px 20px', textAlign: 'start' }}>טלפון</th>
                <th style={{ padding: '14px 20px', textAlign: 'start' }}>סטטוס</th>
                <th style={{ padding: '14px 20px', textAlign: 'start' }}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u: TeamUser) => {
                const editable = canManage(myRole, u.role);
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--hair)' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ fontWeight: 600 }}>{u.name ?? '—'}</div>
                      <div style={{ color: 'var(--text-dim)', fontSize: 13 }} dir="ltr">{u.email}</div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      {editable ? (
                        <select
                          style={{ ...input, height: 34, minWidth: 120 }}
                          value={u.role}
                          onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value as Role } })}
                        >
                          <option value="agent">סוכן</option>
                          <option value="admin" disabled={myRole !== 'owner'}>מנהל</option>
                          <option value="owner" disabled={myRole !== 'owner'}>בעלים</option>
                        </select>
                      ) : (
                        <span className={`chip ${ROLE_CHIP[u.role]}`}>{ROLE_LABEL[u.role]}</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 20px', color: 'var(--text-dim)' }} dir="ltr">{u.phoneHint ?? '—'}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span className={`chip ${u.isActive ? 'green' : 'muted'}`}>{u.isActive ? 'פעיל' : 'מושבת'}</span>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      {editable && (
                        <button
                          className="btn ghost"
                          onClick={() => update.mutate({ id: u.id, body: { isActive: !u.isActive } })}
                          disabled={update.isPending}
                        >
                          {u.isActive ? 'השבת' : 'הפעל'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {toast && <div className="toast"><span className="d" /> {toast}</div>}
    </>
  );
}
